import { API } from "@/config/apiBase";
import React, { useState, useEffect, useContext } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Phone, ArrowLeft, Calendar as CalendarIcon, Plus, Gift, PhoneCall, 
  ChevronLeft, ChevronRight, Bell, Sparkles, Heart, Users, X, Check
} from "lucide-react";
import CallButton from "@/components/CallButton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import axios from "axios";
import { AuthContext } from "@/App";

/**
 * Format a date string (YYYY-MM-DD) without timezone conversion
 * This prevents dates from shifting when displayed in different timezones
 */
const formatDateString = (dateStr, options = {}) => {
  if (!dateStr) return "";
  // Parse the date string directly without creating a Date object
  const [year, month, day] = dateStr.split("-").map(Number);
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const weekdays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  
  // Create date at noon to avoid any timezone edge cases
  const date = new Date(year, month - 1, day, 12, 0, 0);
  
  if (options.short) {
    // "Mon, Jan 7"
    return `${weekdays[date.getDay()]}, ${months[month - 1]} ${day}`;
  }
  if (options.monthDay) {
    // "Jan 7"
    return `${months[month - 1]} ${day}`;
  }
  // Default: "1/7/2025"
  return `${month}/${day}/${year}`;
};


const CalendarPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useContext(AuthContext) || {};
  
  // Read URL params for pre-selection
  const preSelectedContactId = searchParams.get("contact");
  const shouldOpenAddOccasion = searchParams.get("addOccasion") === "true";
  
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [allEvents, setAllEvents] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [eventTypes, setEventTypes] = useState({});
  const [holidays, setHolidays] = useState({});
  const [holidaySubs, setHolidaySubs] = useState({});
  const [holidayAssociations, setHolidayAssociations] = useState({});
  const [callPrompts, setCallPrompts] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  
  // Modal states
  const [showAddEvent, setShowAddEvent] = useState(false);
  const [showCallPrompts, setShowCallPrompts] = useState(false);
  const [showHolidays, setShowHolidays] = useState(false);
  const [showHolidayContacts, setShowHolidayContacts] = useState(false);
  const [selectedHolidayForContacts, setSelectedHolidayForContacts] = useState(null);
  const [contactSearch, setContactSearch] = useState("");
  const [selectedEvent, setSelectedEvent] = useState(null);
  
  // Form states
  const [newEvent, setNewEvent] = useState({
    contact_id: preSelectedContactId || "",
    event_type: "birthday",
    date: "",
    recurring: true,
    reminder_days_before: 7,
    notes: ""
  });

  // Auto-open add dialog if URL param is set
  useEffect(() => {
    if (shouldOpenAddOccasion && !isLoading) {
      setShowAddEvent(true);
      // Clear URL params after opening
      navigate("/calendar", { replace: true });
    }
  }, [shouldOpenAddOccasion, isLoading, navigate]);

  // Update form when pre-selected contact changes
  useEffect(() => {
    if (preSelectedContactId) {
      setNewEvent(prev => ({ ...prev, contact_id: preSelectedContactId }));
    }
  }, [preSelectedContactId]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [upcomingRes, eventsRes, contactsRes, eventTypesRes, holidaysRes, holidaySubsRes, holidayAssocRes] = await Promise.all([
        axios.get(`${API}/events/upcoming?days=60`),
        axios.get(`${API}/events`),
        axios.get(`${API}/contacts`),
        axios.get(`${API}/event-types`),
        axios.get(`${API}/holidays`),
        axios.get(`${API}/holidays/subscriptions`),
        axios.get(`${API}/holidays/associations`)
      ]);
      
      setUpcomingEvents(upcomingRes.data);
      setAllEvents(eventsRes.data);
      setContacts(contactsRes.data);
      setEventTypes(eventTypesRes.data);
      setHolidays(holidaysRes.data);
      setHolidaySubs(holidaySubsRes.data);
      setHolidayAssociations(holidayAssocRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddEvent = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/events`, newEvent);
      toast.success("Event added!");
      setShowAddEvent(false);
      setNewEvent({
        contact_id: "",
        event_type: "birthday",
        date: "",
        recurring: true,
        reminder_days_before: 7,
        notes: ""
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add event");
    }
  };

  const handleDeleteEvent = async (eventId) => {
    try {
      await axios.delete(`${API}/events/${eventId}`);
      toast.success("Event deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete event");
    }
  };

  const handleToggleHoliday = async (holidayId, enabled) => {
    try {
      await axios.post(`${API}/holidays/subscribe`, {
        holiday_id: holidayId,
        enabled: enabled,
        reminder_days_before: 3
      });
      setHolidaySubs({
        ...holidaySubs,
        [holidayId]: { ...holidaySubs[holidayId], enabled }
      });
      toast.success(enabled ? "Holiday reminder enabled" : "Holiday reminder disabled");
      fetchData(); // Refresh to update upcoming events
    } catch (error) {
      toast.error("Failed to update holiday");
    }
  };

  const openHolidayContacts = (holidayId) => {
    setSelectedHolidayForContacts(holidayId);
    setContactSearch("");
    setShowHolidayContacts(true);
  };

  const handleAddContactToHoliday = async (contactId) => {
    if (!selectedHolidayForContacts) return;
    try {
      await axios.post(`${API}/holidays/associations`, {
        holiday_id: selectedHolidayForContacts,
        contact_id: contactId
      });
      // Update local state
      setHolidayAssociations(prev => ({
        ...prev,
        [selectedHolidayForContacts]: [...(prev[selectedHolidayForContacts] || []), contactId]
      }));
      toast.success("Contact added to holiday");
      fetchData();
    } catch (error) {
      toast.error("Failed to add contact");
    }
  };

  const handleRemoveContactFromHoliday = async (contactId) => {
    if (!selectedHolidayForContacts) return;
    try {
      await axios.delete(`${API}/holidays/associations`, {
        data: {
          holiday_id: selectedHolidayForContacts,
          contact_id: contactId
        }
      });
      // Update local state
      setHolidayAssociations(prev => ({
        ...prev,
        [selectedHolidayForContacts]: (prev[selectedHolidayForContacts] || []).filter(id => id !== contactId)
      }));
      toast.success("Contact removed from holiday");
      fetchData();
    } catch (error) {
      toast.error("Failed to remove contact");
    }
  };

  const getContactById = (contactId) => {
    return contacts.find(c => c.contact_id === contactId);
  };

  const fetchCallPrompts = async (eventType) => {
    try {
      const response = await axios.get(`${API}/call-prompts/${eventType}`);
      setCallPrompts(response.data);
      setShowCallPrompts(true);
    } catch (error) {
      console.error("Error fetching prompts:", error);
    }
  };

  const getDaysUntilText = (days) => {
    if (days === 0) return "Today!";
    if (days === 1) return "Tomorrow";
    if (days < 7) return `In ${days} days`;
    if (days < 14) return `In ${Math.floor(days / 7)} week`;
    return `In ${Math.floor(days / 7)} weeks`;
  };

  const getEventColor = (days, eventColor) => {
    if (days === 0) return "#E07A5F";
    if (days <= 3) return "#F2CC8F";
    return eventColor || "#81B29A";
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-[#E07A5F] border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      {/* Header */}
      <header className="bg-white border-b border-[#F4F1EA] sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/dashboard" data-testid="back-link">
                <Button variant="ghost" size="icon" className="rounded-full">
                  <ArrowLeft className="w-5 h-5 text-[#3D405B]" />
                </Button>
              </Link>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-[#E07A5F] rounded-full flex items-center justify-center">
                  <CalendarIcon className="w-4 h-4 text-white" />
                </div>
                <span className="font-bold text-lg text-[#3D405B]" style={{ fontFamily: 'Fraunces, serif' }}>
                  Occasions
                </span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={() => setShowHolidays(true)}
                className="border-[#F4F1EA] rounded-full"
                data-testid="holidays-button"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                Holidays
              </Button>
              <Button
                onClick={() => setShowAddEvent(true)}
                className="bg-[#E07A5F] hover:bg-[#D0694E] text-white rounded-full"
                data-testid="add-event-button"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Event
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        {/* Upcoming Events */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-[#3D405B] mb-4 flex items-center gap-2" style={{ fontFamily: 'Fraunces, serif' }}>
            <Bell className="w-5 h-5 text-[#E07A5F]" />
            Upcoming in Next 60 Days
          </h2>
          
          {upcomingEvents.length === 0 ? (
            <div className="bg-white rounded-2xl p-8 text-center border border-[#F4F1EA]">
              <CalendarIcon className="w-12 h-12 text-[#9CA3AF] mx-auto mb-4" />
              <p className="text-[#6D6F7C]">No upcoming events</p>
              <p className="text-sm text-[#9CA3AF] mt-1">Add birthdays and special occasions to get reminders</p>
            </div>
          ) : (
            <div className="space-y-3">
              {upcomingEvents.map((event, index) => {
                const isHoliday = event.source === "holiday";
                const needsContacts = event.needs_contacts;
                
                return (
                <motion.div
                  key={event.event_id || event.holiday_id || `${event.contact_id}-${event.event_type}-${index}`}
                  className={`bg-white rounded-2xl p-4 border border-[#F4F1EA] hover:shadow-md transition-all ${
                    event.is_deceased ? 'bg-[#6D6F7C]/5' : ''
                  }`}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <div className="flex items-center gap-4">
                    {/* Event Icon */}
                    <div 
                      className="w-14 h-14 rounded-2xl flex items-center justify-center text-2xl flex-shrink-0"
                      style={{ backgroundColor: `${getEventColor(event.days_until, event.event_color)}20` }}
                    >
                      {event.event_icon}
                    </div>
                    
                    {/* Event Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-[#3D405B] truncate">
                          {isHoliday 
                            ? (event.contact_name 
                                ? `${event.event_name} — Call ${event.contact_name}`
                                : event.event_name)
                            : `${event.contact_name}'s ${event.event_name}`
                          }
                        </h3>
                        {event.is_deceased && (
                          <Badge variant="secondary" className="bg-[#6D6F7C]/10 text-[#6D6F7C] text-xs">
                            Memorial
                          </Badge>
                        )}
                        {needsContacts && (
                          <Badge variant="secondary" className="bg-[#F2CC8F]/30 text-[#3D405B] text-xs">
                            Add people
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-[#6D6F7C]">
                        {formatDateString(event.date, { short: true })}
                      </p>
                    </div>
                    
                    {/* Days Until Badge */}
                    <div className="text-right flex-shrink-0">
                      <Badge 
                        className={`${
                          event.days_until === 0 ? 'bg-[#E07A5F] text-white' :
                          event.days_until <= 3 ? 'bg-[#F2CC8F] text-[#3D405B]' :
                          'bg-[#81B29A]/20 text-[#81B29A]'
                        }`}
                      >
                        {getDaysUntilText(event.days_until)}
                      </Badge>
                    </div>
                    
                    {/* Action Buttons - Only show for events with contacts */}
                    {event.contact_id && (
                      <div className="flex gap-2 flex-shrink-0">
                        <CallButton
                          phone={contacts.find(c => c.contact_id === event.contact_id)?.phone}
                          contactId={event.contact_id}
                          contactName={event.contact_name}
                          variant="icon"
                          size="sm"
                          data-testid={`call-event-${event.contact_id}`}
                        />
                        <Button
                          size="sm"
                          variant="outline"
                          className="rounded-full border-[#F4F1EA]"
                          onClick={() => {
                            setSelectedEvent(event);
                            fetchCallPrompts(event.event_type);
                          }}
                          data-testid={`prompts-${event.contact_id}`}
                        >
                          <Sparkles className="w-4 h-4" />
                        </Button>
                        <Link to={`/gifts?contact=${event.contact_id}&occasion=${event.event_type}`}>
                          <Button
                            size="sm"
                            variant="outline"
                            className="rounded-full border-[#F4F1EA]"
                          >
                            <Gift className="w-4 h-4" />
                          </Button>
                        </Link>
                      </div>
                    )}
                    
                    {/* Holiday without contacts - show "Add people" button */}
                    {isHoliday && needsContacts && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="rounded-full border-[#F2CC8F] text-[#3D405B]"
                        onClick={() => {
                          setSelectedHolidayForContacts(event.holiday_id);
                          setContactSearch("");
                          setShowHolidayContacts(true);
                        }}
                      >
                        <Users className="w-4 h-4 mr-1" />
                        Add people
                      </Button>
                    )}
                  </div>
                </motion.div>
              )})}
            </div>
          )}
        </div>

        {/* All Events by Category */}
        <div>
          <h2 className="text-xl font-semibold text-[#3D405B] mb-4 flex items-center gap-2" style={{ fontFamily: 'Fraunces, serif' }}>
            <CalendarIcon className="w-5 h-5 text-[#81B29A]" />
            All Saved Events
          </h2>
          
          {allEvents.length === 0 ? (
            <div className="bg-white rounded-2xl p-8 text-center border border-[#F4F1EA]">
              <div className="w-16 h-16 bg-[#F4F1EA] rounded-full flex items-center justify-center mx-auto mb-4">
                <CalendarIcon className="w-8 h-8 text-[#9CA3AF]" />
              </div>
              <h3 className="font-semibold text-[#3D405B] mb-2" style={{ fontFamily: 'Fraunces, serif' }}>No events yet</h3>
              <p className="text-sm text-[#6D6F7C] mb-4">Add birthdays and special occasions to never miss a moment.</p>
              <Button 
                onClick={() => setShowAddEvent(true)}
                className="bg-[#E07A5F] hover:bg-[#D0694E] text-white rounded-full"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Event
              </Button>
            </div>
          ) : (
            <div className="grid gap-3">
              {allEvents.map((event, index) => (
                <motion.div
                  key={event.event_id}
                  className="bg-white rounded-xl p-4 border border-[#F4F1EA] flex items-center gap-4"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.03 }}
                >
                  <span className="text-xl">{event.event_icon}</span>
                  <div className="flex-1">
                    <p className="font-medium text-[#3D405B]">
                      {contacts.find(c => c.contact_id === event.contact_id)?.name || 'Unknown'} - {event.event_name}
                    </p>
                    <p className="text-sm text-[#6D6F7C]">
                      {formatDateString(event.date)} {event.recurring && '(Yearly)'}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <CallButton
                      phone={contacts.find(c => c.contact_id === event.contact_id)?.phone}
                      contactId={event.contact_id}
                      contactName={contacts.find(c => c.contact_id === event.contact_id)?.name}
                      variant="icon"
                      size="sm"
                      data-testid={`call-all-event-${event.event_id}`}
                    />
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDeleteEvent(event.event_id)}
                      className="text-[#E07A5F] hover:bg-[#E07A5F]/10"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Add Event Modal */}
      <Dialog open={showAddEvent} onOpenChange={setShowAddEvent}>
        <DialogContent className="bg-white rounded-3xl border-[#F4F1EA] max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-[#3D405B]" style={{ fontFamily: 'Fraunces, serif' }}>
              Add Special Occasion
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddEvent} className="space-y-4">
            <div>
              <Label className="text-[#3D405B]">Contact *</Label>
              <Select value={newEvent.contact_id} onValueChange={(val) => setNewEvent({ ...newEvent, contact_id: val })}>
                <SelectTrigger className="bg-white border-2 border-[#F4F1EA] rounded-xl mt-1" data-testid="event-contact">
                  <SelectValue placeholder="Select a contact" />
                </SelectTrigger>
                <SelectContent className="bg-white border-[#F4F1EA] rounded-xl">
                  {contacts.map(contact => (
                    <SelectItem key={contact.contact_id} value={contact.contact_id}>
                      {contact.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-[#3D405B]">Event Type *</Label>
              <Select value={newEvent.event_type} onValueChange={(val) => setNewEvent({ ...newEvent, event_type: val })}>
                <SelectTrigger className="bg-white border-2 border-[#F4F1EA] rounded-xl mt-1" data-testid="event-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white border-[#F4F1EA] rounded-xl max-h-60">
                  {Object.entries(eventTypes).map(([key, type]) => (
                    <SelectItem key={key} value={key}>
                      <span className="flex items-center gap-2">
                        <span>{type.icon}</span>
                        {type.name}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-[#3D405B]">Date *</Label>
              <Input
                type="date"
                value={newEvent.date}
                onChange={(e) => setNewEvent({ ...newEvent, date: e.target.value })}
                className="bg-white border-2 border-[#F4F1EA] rounded-xl mt-1"
                required
                data-testid="event-date"
              />
            </div>

            <div>
              <Label className="text-[#3D405B]">Remind me</Label>
              <Select 
                value={String(newEvent.reminder_days_before)} 
                onValueChange={(val) => setNewEvent({ ...newEvent, reminder_days_before: parseInt(val) })}
              >
                <SelectTrigger className="bg-white border-2 border-[#F4F1EA] rounded-xl mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white border-[#F4F1EA] rounded-xl">
                  <SelectItem value="1">1 day before</SelectItem>
                  <SelectItem value="3">3 days before</SelectItem>
                  <SelectItem value="7">1 week before</SelectItem>
                  <SelectItem value="14">2 weeks before</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <Label className="text-[#3D405B]">Repeats yearly</Label>
                <p className="text-xs text-[#9CA3AF]">Get reminded every year</p>
              </div>
              <Switch
                checked={newEvent.recurring}
                onCheckedChange={(checked) => setNewEvent({ ...newEvent, recurring: checked })}
              />
            </div>

            <div>
              <Label className="text-[#3D405B]">Notes (optional)</Label>
              <Textarea
                value={newEvent.notes}
                onChange={(e) => setNewEvent({ ...newEvent, notes: e.target.value })}
                className="bg-white border-2 border-[#F4F1EA] rounded-xl mt-1"
                placeholder="Any special notes..."
                data-testid="event-notes"
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowAddEvent(false)} className="flex-1 rounded-full border-[#F4F1EA]">
                Cancel
              </Button>
              <Button type="submit" className="flex-1 bg-[#E07A5F] hover:bg-[#D0694E] text-white rounded-full" data-testid="save-event-button">
                Add Event
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Call Prompts Modal */}
      <Dialog open={showCallPrompts} onOpenChange={setShowCallPrompts}>
        <DialogContent className="bg-white rounded-3xl border-[#F4F1EA] max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-[#3D405B]" style={{ fontFamily: 'Fraunces, serif' }}>
              {selectedEvent && `${selectedEvent.event_icon} Call Tips`}
            </DialogTitle>
          </DialogHeader>
          
          {callPrompts && selectedEvent && (
            <div className="space-y-6">
              <div>
                <h3 className="font-semibold text-[#3D405B] mb-2 flex items-center gap-2">
                  <PhoneCall className="w-4 h-4 text-[#81B29A]" />
                  Conversation Starters
                </h3>
                <ul className="space-y-2">
                  {callPrompts.openers.map((opener, i) => (
                    <li key={i} className="bg-[#FDFBF7] rounded-xl p-3 text-[#3D405B] text-sm">
                      "{opener}"
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h3 className="font-semibold text-[#3D405B] mb-2 flex items-center gap-2">
                  <Heart className="w-4 h-4 text-[#E07A5F]" />
                  Topics to Discuss
                </h3>
                <div className="flex flex-wrap gap-2">
                  {callPrompts.topics.map((topic, i) => (
                    <Badge key={i} variant="secondary" className="bg-[#F4F1EA] text-[#3D405B]">
                      {topic}
                    </Badge>
                  ))}
                </div>
              </div>

              <div>
                <h3 className="font-semibold text-[#3D405B] mb-2 flex items-center gap-2">
                  <Gift className="w-4 h-4 text-[#F2CC8F]" />
                  Gift Ideas
                </h3>
                <Link to={`/gifts?contact=${selectedEvent.contact_id}`}>
                  <Button className="w-full bg-[#81B29A] hover:bg-[#71A28A] text-white rounded-full">
                    Browse Gift Shop
                  </Button>
                </Link>
              </div>

              {/* Call Now Button */}
              <div className="pt-4 border-t border-[#F4F1EA]">
                <CallButton
                  phone={contacts.find(c => c.contact_id === selectedEvent.contact_id)?.phone}
                  contactId={selectedEvent.contact_id}
                  contactName={selectedEvent.contact_name}
                  variant="primary"
                  label="Call Now"
                  className="w-full"
                  data-testid="call-from-prompts"
                />
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Holidays Modal */}
      <Dialog open={showHolidays} onOpenChange={setShowHolidays}>
        <DialogContent className="bg-white rounded-3xl border-[#F4F1EA] max-w-md max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-[#3D405B]" style={{ fontFamily: 'Fraunces, serif' }}>
              Holiday Reminders
            </DialogTitle>
          </DialogHeader>
          
          <p className="text-sm text-[#6D6F7C] mb-4">
            Turn on a holiday, then choose who you want to reach out to.
          </p>

          <div className="space-y-3">
            {Object.entries(holidaySubs).map(([holidayId, holiday]) => {
              const contactCount = holiday.contact_count || 0;
              return (
                <div key={holidayId} className="p-3 bg-[#FDFBF7] rounded-xl">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">{holiday.icon}</span>
                      <div>
                        <span className="font-medium text-[#3D405B]">{holiday.name}</span>
                        {holiday.enabled && (
                          <p className="text-xs text-[#6D6F7C]">
                            {contactCount === 0 ? "No people added" : `${contactCount} ${contactCount === 1 ? 'person' : 'people'}`}
                          </p>
                        )}
                      </div>
                    </div>
                    <Switch
                      checked={holiday.enabled}
                      onCheckedChange={(checked) => handleToggleHoliday(holidayId, checked)}
                    />
                  </div>
                  {holiday.enabled && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openHolidayContacts(holidayId)}
                      className="mt-2 w-full text-xs border-[#F4F1EA] text-[#3D405B]"
                    >
                      <Users className="w-3 h-3 mr-1" />
                      {contactCount === 0 ? "Add people" : "Manage people"}
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>

      {/* Holiday Contacts Modal */}
      <Dialog open={showHolidayContacts} onOpenChange={setShowHolidayContacts}>
        <DialogContent className="bg-white rounded-3xl border-[#F4F1EA] max-w-md max-h-[80vh] flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="text-xl font-bold text-[#3D405B]" style={{ fontFamily: 'Fraunces, serif' }}>
              {selectedHolidayForContacts && holidaySubs[selectedHolidayForContacts] && (
                <>
                  <span className="mr-2">{holidaySubs[selectedHolidayForContacts].icon}</span>
                  {holidaySubs[selectedHolidayForContacts].name}
                </>
              )}
            </DialogTitle>
          </DialogHeader>
          
          <p className="text-sm text-[#6D6F7C] mb-3">
            Who do you want to reach out to on this holiday?
          </p>

          {/* Selected contacts */}
          {selectedHolidayForContacts && (holidayAssociations[selectedHolidayForContacts] || []).length > 0 && (
            <div className="mb-4">
              <p className="text-xs text-[#6D6F7C] mb-2">Selected:</p>
              <div className="flex flex-wrap gap-2">
                {(holidayAssociations[selectedHolidayForContacts] || []).map(contactId => {
                  const contact = getContactById(contactId);
                  if (!contact) return null;
                  return (
                    <Badge 
                      key={contactId}
                      className="bg-[#81B29A] text-white flex items-center gap-1 px-2 py-1"
                    >
                      {contact.name}
                      <X 
                        className="w-3 h-3 cursor-pointer hover:text-red-200" 
                        onClick={() => handleRemoveContactFromHoliday(contactId)}
                      />
                    </Badge>
                  );
                })}
              </div>
            </div>
          )}

          {/* Search */}
          <Input
            placeholder="Search contacts..."
            value={contactSearch}
            onChange={(e) => setContactSearch(e.target.value)}
            className="mb-3 border-[#F4F1EA]"
          />

          {/* Contact list */}
          <div className="overflow-y-auto flex-1 space-y-2">
            {contacts
              .filter(c => 
                c.name.toLowerCase().includes(contactSearch.toLowerCase()) &&
                !(holidayAssociations[selectedHolidayForContacts] || []).includes(c.contact_id)
              )
              .map(contact => (
                <div 
                  key={contact.contact_id}
                  className="flex items-center justify-between p-2 hover:bg-[#FDFBF7] rounded-lg cursor-pointer"
                  onClick={() => handleAddContactToHoliday(contact.contact_id)}
                >
                  <span className="text-sm text-[#3D405B]">{contact.name}</span>
                  <Plus className="w-4 h-4 text-[#81B29A]" />
                </div>
              ))
            }
            {contacts.filter(c => 
              c.name.toLowerCase().includes(contactSearch.toLowerCase()) &&
              !(holidayAssociations[selectedHolidayForContacts] || []).includes(c.contact_id)
            ).length === 0 && (
              <p className="text-center text-sm text-[#6D6F7C] py-4">
                {contactSearch ? "No contacts found" : "All contacts added"}
              </p>
            )}
          </div>

          <Button
            onClick={() => setShowHolidayContacts(false)}
            className="mt-4 bg-[#E07A5F] hover:bg-[#D0694E] text-white rounded-full"
          >
            Done
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CalendarPage;
